import { styled } from "@mui/material/styles";
import { Box, Typography, Card, CardContent, CardMedia, Grid } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import { Layout } from "../../components/layout/layout";
import { GlobalFirstSection, GlobalSpacing } from "../../components/layout/styles";
import { product_hero1Img, product_hero2Img, product_hero3Img, product_hero4Img, product_hero5Img } from "../../assets/image";
import { mockProducts } from "./mock.data";
import { useI18n } from "react-simple-i18n";
import { useGlobalContext } from "../../provider";

const heroImages = [product_hero1Img, product_hero2Img, product_hero3Img, product_hero4Img, product_hero5Img];

const Product = () => {
    const { t } = useI18n();
    const [globalState]: GlobalContextType = useGlobalContext();

    const navigate = useNavigate();
    const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [slideDirection, setSlideDirection] = useState<'left' | 'right'>('right');
    const [nextImageIndex, setNextImageIndex] = useState(1);

    useEffect(() => {
        const timer = setInterval(() => {
            setSlideDirection('right');
            setIsTransitioning(true);
            setNextImageIndex((currentHeroIndex + 1) % heroImages.length);
            setCurrentHeroIndex((prev) => (prev + 1) % heroImages.length);
            setIsTransitioning(false);
        }, 5000);

        return () => clearInterval(timer);
    }, [currentHeroIndex]);

    const handleProductClick = (productId: string) => {
        const productIndex = mockProducts.findIndex(product => product.id === productId);
        if (productIndex !== -1) {
            setSlideDirection(productIndex > currentHeroIndex ? 'right' : 'left');
            setIsTransitioning(true);
            setNextImageIndex(productIndex);
            setTimeout(() => {
                setCurrentHeroIndex(productIndex);
                setIsTransitioning(false);
                navigate(`/product/${productId}`, {
                    state: { heroImageIndex: productIndex }
                });
            }, 100);
        }
    };

    const handleDotClick = (index: number) => {
        if (index !== currentHeroIndex) {
            setSlideDirection(index > currentHeroIndex ? 'right' : 'left');
            setIsTransitioning(true);
            setNextImageIndex(index);
            setTimeout(() => {
                setCurrentHeroIndex(index);
                setIsTransitioning(false);
            }, 1000);
        }
    };

    return (
        <Layout>
            <HeroWrapper>
                <HeroSection>
                    <HeroImageContainer>
                        <HeroImage
                            src={heroImages[currentHeroIndex]}
                            alt="Hero"
                            isTransitioning={isTransitioning}
                            slideDirection={slideDirection}
                            isCurrent={true}
                        />
                        <HeroImage
                            src={heroImages[nextImageIndex]}
                            alt="Hero Next"
                            isTransitioning={isTransitioning}
                            slideDirection={slideDirection}
                            isCurrent={false}
                        />
                    </HeroImageContainer>
                    <HeroOverlay>
                        <Typography variant="h2" color="common.white">
                            {t("product.title")}
                        </Typography>
                        <Typography variant="h5" color="common.white">
                            {t("product.subtitle")}
                        </Typography>
                    </HeroOverlay>
                    <HeroFooter>
                        <ImageCount>
                            {heroImages.map((_, index) => (
                                <ImageDot
                                    key={index}
                                    isActive={index === currentHeroIndex}
                                    onClick={() => handleDotClick(index)}
                                />
                            ))}
                        </ImageCount>
                    </HeroFooter>
                </HeroSection>
            </HeroWrapper>

            <ProductWrapper>
                <GlobalSpacing>
                    <ProductsSection>
                        <Typography variant="h2" gutterBottom>
                            {t("product.ourProducts")}
                        </Typography>

                        <Grid container spacing={4}>
                            {mockProducts.map((product) => (
                                <Grid item xs={12} sm={6} md={4} key={product.id}>
                                    <ProductCard onClick={() => handleProductClick(product.id)}>
                                        <CardMedia
                                            component="img"
                                            height="200"
                                            image={product.images[0]}
                                            alt={product.title}
                                        />
                                        <CardContent>
                                            <Typography variant="h6" gutterBottom>
                                                {globalState.lang === "en" ? product.title : product.jpTitle}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                                {globalState.lang === "en" ? product.description : product.jpDescription}
                                            </Typography>
                                            <Typography variant="h6" color="primary">
                                                ${product.price}
                                            </Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    {t("product.rating", product.rating.toString(), product.reviews.toString())}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </ProductCard>
                                </Grid>
                            ))}
                        </Grid>
                    </ProductsSection>
                </GlobalSpacing>
            </ProductWrapper>
        </Layout>
    );
};

const HeroWrapper = styled(GlobalFirstSection)(({ theme }) => ({
    position: 'relative',
    marginBottom: theme.spacing(5),
}));

const HeroSection = styled(Box)(({ theme }) => ({
    position: 'relative',
    height: '100%',
    width: '100%',
    overflow: 'hidden',
}));

const HeroImageContainer = styled(Box)({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: 'hidden',
});

const HeroImage = styled('img')<{
    isTransitioning: boolean;
    slideDirection: 'left' | 'right';
    isCurrent: boolean;
}>(({ isTransitioning, slideDirection, isCurrent }) => ({
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    transform: isTransitioning
        ? `translateX(${isCurrent
            ? (slideDirection === 'left' ? '100%' : '-100%')
            : '0'
        }) scale(1.1)`
        : `translateX(${isCurrent ? '0' : (slideDirection === 'left' ? '-100%' : '100%')}) scale(1)`,
    opacity: isTransitioning ? 1 : (isCurrent ? 1 : 0),
    transition: 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
    filter: 'brightness(0.8)',
    '&:hover': {
        filter: 'brightness(1)',
        transform: isTransitioning
            ? `translateX(${isCurrent
                ? (slideDirection === 'left' ? '100%' : '-100%')
                : '0'
            }) scale(1.15)`
            : `translateX(${isCurrent ? '0' : (slideDirection === 'left' ? '-100%' : '100%')}) scale(1.05)`,
    }
}));

const HeroOverlay = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    background: 'rgba(0, 0, 0, 0.4)',
    color: 'white',
    textAlign: 'center',
    padding: theme.spacing(4),
}));

const HeroFooter = styled(Box)(({ theme }) => ({
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
    background: 'linear-gradient(transparent, rgba(0,0,0,0.5))',
}));

const ImageCount = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1.5),
}));

const ImageDot = styled(Box)<{ isActive: boolean }>(({ isActive, theme }) => ({
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    backgroundColor: isActive ? 'white' : 'rgba(255,255,255,0.5)',
    cursor: 'pointer',
    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
        backgroundColor: 'white',
        transform: 'scale(1.3)',
    },
}));

const ProductWrapper = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: "column",
    gap: theme.spacing(5),
    padding: `${theme.spacing(5)} 0px`,

    [theme.breakpoints.down("md")]: {
        gap: theme.spacing(3),
        padding: `${theme.spacing(3)} 0px`,
    }
}));

const ProductsSection = styled(Box)(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(4),

    [theme.breakpoints.down("md")]: {
        gap: theme.spacing(1),
        padding: theme.spacing(0)
    }
}));

const ProductCard = styled(Card)(({ theme }) => ({
    height: '100%',
    cursor: 'pointer',
    transition: 'transform 0.2s ease-in-out',
    '&:hover': {
        transform: 'translateY(-4px)',
    },
}));

export { Product };